import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, MapPin, ArrowRight, Laptop } from 'lucide-react';
import { Helmet } from 'react-helmet-async';

const LadyBirdDeedOrlando = () => {
  return (
    <>
      <Helmet>
        <title>Lady Bird Deed Attorney Orlando FL — Remote Statewide Service</title>
        <meta name="description" content="Orlando Lady Bird Deed attorney. Get your Lady Bird Deed done remotely — no travel required. Serving Orange County & all Florida. Call 941-206-2223." />
        <link rel="canonical" href="https://thelawyerlady.com/lady-bird-deed-orlando-fl" />
        <meta property="og:title" content="Lady Bird Deed Attorney Orlando FL — Remote Statewide Service" />
        <meta property="og:description" content="Orlando Lady Bird Deed attorney. Get your Lady Bird Deed done remotely — no travel required. Serving Orange County & all Florida." />
        <meta property="og:url" content="https://thelawyerlady.com/lady-bird-deed-orlando-fl" />
        <meta property="og:type" content="website" />
        
        <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          "itemListElement": [
            { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://thelawyerlady.com" },
            { "@type": "ListItem", "position": 2, "name": "Lady Bird Deed Florida", "item": "https://thelawyerlady.com/lady-bird-deed-florida" },
            { "@type": "ListItem", "position": 3, "name": "Orlando", "item": "https://thelawyerlady.com/lady-bird-deed-orlando-fl" }
          ]
        })}
        </script>
        
        <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Attorney",
          "name": "Season A. Spanski",
          "alternateName": "Spanski Law, PLLC",
          "url": "https://thelawyerlady.com",
          "telephone": "+1-941-206-2223",
          "email": "contact@thelawyerlady.com",
          "address": { "@type": "PostalAddress", "addressLocality": "Venice", "addressRegion": "FL", "addressCountry": "US" },
          "areaServed": [{ "@type": "City", "name": "Orlando" }, { "@type": "County", "name": "Orange County" }, { "@type": "State", "name": "Florida" }],
          "priceRange": "$800-$1000"
        })}
        </script>
      </Helmet>

      <div className="min-h-screen bg-[#0a0a0a]">
        {/* Breadcrumb */}
        <div className="max-w-4xl mx-auto px-6 pt-24 pb-4">
          <nav className="flex items-center gap-2 text-sm text-[#faf9f7]/60">
            <Link to="/" className="hover:text-[#c9a87c] transition-colors">Home</Link>
            <span>/</span>
            <Link to="/lady-bird-deed-florida" className="hover:text-[#c9a87c] transition-colors">Lady Bird Deed Florida</Link>
            <span>/</span>
            <span className="text-[#c9a87c]">Orlando</span>
          </nav>
        </div>

        {/* Hero Section */}
        <section className="max-w-4xl mx-auto px-6 py-12">
          <h1 className="font-serif text-4xl md:text-5xl text-[#faf9f7] mb-6">
            Lady Bird Deed Attorney Serving Orlando FL — Remote Statewide Service
          </h1>
          <p className="font-sans text-xl text-[#faf9f7]/70 mb-8 leading-relaxed">
            I'm Season Spanski, a Florida attorney who helps Orlando homeowners get Lady Bird Deeds done without ever leaving home. My office is in Venice, but I serve all 67 Florida counties remotely.
          </p>
          
          <div className="flex flex-wrap gap-4 mb-8">
            <a href="tel:941-206-2223" className="btn-elegant inline-flex items-center gap-2 px-6 py-3 bg-[#c9a87c] text-[#0a0a0a] font-sans font-semibold rounded-sm hover:bg-[#d4b896]">
              <Phone className="w-5 h-5" />
              Call 941-206-2223
            </a>
            <Link to="/#contact" className="btn-elegant inline-flex items-center gap-2 px-6 py-3 border border-[#c9a87c] text-[#c9a87c] font-sans font-medium rounded-sm hover:bg-[#c9a87c]/10">
              Free Consultation
              <ArrowRight className="w-5 h-5" />
            </Link>
          </div>

          <div className="flex flex-wrap gap-6 text-sm text-[#faf9f7]/60">
            <div className="flex items-center gap-2">
              <Laptop className="w-4 h-4 text-[#c9a87c]" />
              <span>100% Remote Service Available</span>
            </div>
            <div className="flex items-center gap-2">
              <MapPin className="w-4 h-4 text-[#c9a87c]" />
              <span>Serving Orange County & All Florida</span>
            </div>
          </div>
        </section>

        {/* Main Content */}
        <article className="max-w-4xl mx-auto px-6 py-12">
          <div className="prose prose-lg prose-invert max-w-none">
            
            <h2 className="font-serif text-3xl text-[#faf9f7] mb-6">Lady Bird Deeds for Orlando Homeowners</h2>
            <p className="text-[#faf9f7]/80 mb-6 leading-relaxed">
              Orlando is one of Florida's largest metro areas, and homeowners here face the same challenge as everyone else in the state: Florida probate is expensive and time-consuming. A Lady Bird Deed solves that problem for your home.
            </p>
            <p className="text-[#faf9f7]/80 mb-6 leading-relaxed">
              With a Lady Bird Deed, you name who will inherit your property when you pass away, but you keep complete control while you're alive. You can sell, refinance, or change your mind at any time. When you die, the property transfers automatically to your beneficiaries — no probate court required.
            </p>

            <h2 className="font-serif text-3xl text-[#faf9f7] mb-6 mt-12">No Need to Travel — Everything Done Online</h2>
            <p className="text-[#faf9f7]/80 mb-6 leading-relaxed">
              Here's the good news for Orlando homeowners: you don't need to drive to Venice or anywhere else to get a Lady Bird Deed. I handle everything remotely:
            </p>
            <ul className="list-disc pl-6 text-[#faf9f7]/80 space-y-2 mb-6">
              <li>Initial consultation by phone or video</li>
              <li>Deed preparation and review via email</li>
              <li>Signing through remote online notarization (RON)</li>
              <li>I handle recording with Orange County (or whatever county your property is in)</li>
            </ul>
            <p className="text-[#faf9f7]/80 mb-6 leading-relaxed">
              Florida law allows remote online notarization, which means you can sign your Lady Bird Deed from your living room using your computer or tablet. The notary verifies your identity through a secure video call, and the document is legally valid.
            </p>

            <h2 className="font-serif text-3xl text-[#faf9f7] mb-6 mt-12">What It Costs</h2>
            <p className="text-[#faf9f7]/80 mb-6 leading-relaxed">
              My fee for a straightforward Lady Bird Deed is $800-$1,000. This includes everything: consultation, deed drafting, review, and guidance through signing. Recording fees paid to the county are additional — typically $30-50 in Orange County.
            </p>

            <h2 className="font-serif text-3xl text-[#faf9f7] mb-6 mt-12">Serving All of Central Florida</h2>
            <p className="text-[#faf9f7]/80 mb-6 leading-relaxed">
              I work with homeowners throughout the Orlando metro area, including Orange County, Osceola County, Seminole County, and Lake County. If you own property anywhere in Florida, I can help.
            </p>
            <p className="text-[#faf9f7]/80 mb-6 leading-relaxed">
              Learn more about my <Link to="/lady-bird-deed-florida" className="text-[#c9a87c] hover:underline">statewide Lady Bird Deed services</Link>, or see how I help homeowners in <Link to="/lady-bird-deed-tampa-fl" className="text-[#c9a87c] hover:underline">Tampa</Link>, <Link to="/lady-bird-deed-fort-lauderdale" className="text-[#c9a87c] hover:underline">Fort Lauderdale</Link>, and <Link to="/lady-bird-deed-sarasota" className="text-[#c9a87c] hover:underline">Sarasota</Link>.
            </p>

          </div>
        </article>

        {/* CTA Section */}
        <section className="max-w-4xl mx-auto px-6 py-16">
          <div className="bg-gradient-to-br from-[#1a1a1a] to-[#0f0f0f] border border-[#c9a87c]/20 rounded-lg p-8 md:p-12 text-center">
            <h2 className="font-serif text-3xl text-[#faf9f7] mb-4">Ready to Protect Your Orlando Home?</h2>
            <p className="text-[#faf9f7]/70 mb-8 max-w-2xl mx-auto">
              Call 941-206-2223 or schedule a free consultation online. I'll explain exactly how a Lady Bird Deed works and whether it's right for your situation.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href="tel:941-206-2223" className="btn-elegant inline-flex items-center justify-center gap-2 px-8 py-4 bg-[#c9a87c] text-[#0a0a0a] font-sans font-semibold rounded-sm hover:bg-[#d4b896]">
                <Phone className="w-5 h-5" />
                Call 941-206-2223
              </a>
              <Link to="/#contact" className="btn-elegant inline-flex items-center justify-center gap-2 px-8 py-4 border border-[#c9a87c] text-[#c9a87c] font-sans font-medium rounded-sm hover:bg-[#c9a87c]/10">
                Schedule Online
              </Link>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default LadyBirdDeedOrlando;
